/**
 * `VenueAboutSection` — "About the room · EDITORIAL" body section per the
 * `vn-section` block in `docs/design-system/1.0.0/templates/city/venue.html`
 * (~lines 283–291). Renders the editorial blurb split on double newlines
 * into paragraphs at 65ch max width.
 *
 * View takes the raw blurb string. The connected component pulls
 * `VenueDetail.editorialBlurb` and forwards it. The view returns `null`
 * when the blurb is null, empty, or whitespace-only — callers do not
 * need a wrapper conditional.
 *
 * Paragraphs come from the copy or they do not exist (WEB-862). Never infer
 * a break from sentence boundaries, character counts, or line length — that
 * invents structure the editor did not write. A blurb with no blank line is
 * one paragraph because that is what it is; the design's two-`<p>` About
 * section (`templates/city/venue.html` ~L267) is achieved by authoring a
 * blank line in `Location.editorialBlurb`, which `clt-venues.ts` now does
 * for `clt-the-evening-muse`.
 *
 * @since WEB-165
 */
'use client';

import * as React from 'react';
import { cn } from '@/components/atoms/primitives/utils';
import type { VenueDetail } from '@/types/venueDetail';

export interface VenueAboutSectionViewProps {
  blurb: string | null;
  className?: string;
}

/**
 * Split on a blank line only. `\n{2,}` is greedy, so a run of three or more
 * newlines is one separator and yields no empty part; what the `filter` does
 * drop is a leading or trailing break, and a "blank" line that carries
 * whitespace. Either way a stray break degrades to the paragraph count the
 * copy asks for rather than rendering an empty `<p>`.
 */
function splitParagraphs(blurb: string): string[] {
  return blurb
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0);
}

export function VenueAboutSectionView({
  blurb,
  className,
}: VenueAboutSectionViewProps): React.ReactElement | null {
  const headingId = React.useId();
  if (blurb == null) return null;
  const paragraphs = splitParagraphs(blurb);
  if (paragraphs.length === 0) return null;

  return (
    <section
      role="region"
      aria-labelledby={headingId}
      data-testid="venue-about-section"
      className={cn('border-b border-border/60 pb-8 mb-8', className)}
    >
      <h2
        id={headingId}
        className="m-0 mb-4 text-2xl font-bold tracking-tight text-foreground font-display sm:text-3xl"
      >
        About the room
        <small className="ml-3 inline-block text-xs font-medium tracking-[0.08em] text-clt-skyline-glow font-mono">
          · EDITORIAL
        </small>
      </h2>
      {paragraphs.map((paragraph, index) => (
        <p
          key={index}
          data-testid="venue-about-paragraph"
          className="mb-3 max-w-[65ch] text-base leading-normal text-muted-foreground last:mb-0"
        >
          {paragraph}
        </p>
      ))}
    </section>
  );
}

export interface VenueAboutSectionProps {
  detail: Pick<VenueDetail, 'editorialBlurb'>;
  className?: string;
}

export function VenueAboutSection({
  detail,
  className,
}: VenueAboutSectionProps): React.ReactElement | null {
  return <VenueAboutSectionView blurb={detail.editorialBlurb} className={className} />;
}
